import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import {
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Card,
  CircularProgress,
  useTheme,
  useMediaQuery,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  Container,
  List
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { collection, getDocs, deleteDoc, doc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';

function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    image: '',
    order: 0
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'categories'));
      const categoriesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata:', error);
      setError('Kategoriler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditingCategory(null);
    setFormData({ name: '', slug: '', description: '', image: '', order: 0 });
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCategory(null);
    setError('');
    setFormData({ name: '', slug: '', description: '', image: '', order: 0 });
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      description: category.description || '',
      image: category.image || '',
      order: category.order || 0
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'categories', id));
        setSuccess('Kategori başarıyla silindi.');
        fetchCategories();
      } catch (error) {
        console.error('Kategori silinirken hata:', error);
        setError('Kategori silinirken hata oluştu.');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name || !formData.slug) {
        throw new Error('Lütfen gerekli alanları doldurun.');
      }

      const categoryData = {
        ...formData,
        order: Number(formData.order) || 0,
        updatedAt: serverTimestamp()
      };

      if (editingCategory) {
        await updateDoc(doc(db, 'categories', editingCategory.id), categoryData);
        setSuccess('Kategori başarıyla güncellendi.');
      } else {
        await addDoc(collection(db, 'categories'), {
          ...categoryData,
          createdAt: serverTimestamp()
        });
        setSuccess('Kategori başarıyla eklendi.');
      }

      handleClose();
      fetchCategories();
    } catch (error) {
      console.error('Kategori kaydedilirken hata:', error);
      setError(error.message || 'Kategori kaydedilirken hata oluştu.');
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Card elevation={0} sx={{ bgcolor: '#f8f8f8', borderRadius: 2 }}>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              mb: 3 
            }}>
              <Typography variant="h5" component="h1">
                Kategori Yönetimi
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpen}
                sx={{ borderRadius: 2 }}
                fullWidth={isMobile}
              >
                Yeni Kategori Ekle
              </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Mobil Görünüm */}
                {isMobile ? (
                  <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                    {categories.map((category) => (
                      <ListItem 
                        key={category.id}
                        sx={{
                          mb: 1,
                          bgcolor: 'white',
                          borderRadius: 1,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          p: 2
                        }}
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          width: '100%',
                          mb: 1
                        }}>
                          <Avatar 
                            src={category.image} 
                            alt={category.name}
                            variant="rounded"
                            sx={{ mr: 2 }}
                          >
                            {category.name.charAt(0)}
                          </Avatar>
                          <Typography variant="subtitle1">
                            {category.name}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          Sıra: {category.order || 0}
                        </Typography>
                        
                        {category.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {category.description}
                          </Typography>
                        )}
                        
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          mt: 2,
                          width: '100%'
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEdit(category)}
                            fullWidth
                          >
                            Düzenle
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={() => handleDelete(category.id)}
                            color="error"
                            fullWidth
                          >
                            Sil
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  /* Masaüstü Görünüm - Mevcut tablo yapısı */
                  <TableContainer sx={{ 
                    overflowX: 'auto',
                    '& .MuiTable-root': {
                      minWidth: { xs: 'auto', sm: 650 }
                    }
                  }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Sıra</TableCell>
                          <TableCell>Kategori Adı</TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Slug</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Görsel</TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Açıklama</TableCell>
                          <TableCell align="right">İşlemler</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categories
                          .sort((a, b) => (a.order || 0) - (b.order || 0))
                          .map((category) => (
                            <TableRow 
                              key={category.id}
                              sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                            >
                              <TableCell>{category.order || 0}</TableCell>
                              <TableCell>{category.name}</TableCell>
                              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                {category.slug}
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                {category.image ? (
                                  <Box
                                    component="img"
                                    src={category.image}
                                    alt={category.name}
                                    sx={{ 
                                      width: 50, 
                                      height: 50, 
                                      objectFit: 'cover',
                                      borderRadius: 1
                                    }}
                                  />
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                {category.description || '-'}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton 
                                  onClick={() => handleEdit(category)}
                                  size={isMobile ? "small" : "medium"}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton 
                                  onClick={() => handleDelete(category.id)} 
                                  color="error"
                                  size={isMobile ? "small" : "medium"}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </Card>

        <Dialog 
          open={open} 
          onClose={handleClose} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ 
            position: 'sticky', 
            top: 0, 
            bgcolor: 'background.paper',
            zIndex: 1
          }}>
            {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori Ekle'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={3} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Kategori Adı"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Slug"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  helperText="URL'de kullanılacak benzersiz tanımlayıcı"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Görsel URL"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  helperText="Kategori görselinin URL'si"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Sıralama"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                  helperText="Kategorinin görüntülenme sırası"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Açıklama"
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ 
            p: 2,
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            zIndex: 1
          }}>
            <Button onClick={handleClose}>İptal</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingCategory ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
}

export default AdminCategories; 